import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';

// ── Icons ─────────────────────────────────────────────────────────────────────
function Ico({ d, size }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size || 16} height={size || 16}
      viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}
Ico.propTypes = { d: PropTypes.string.isRequired, size: PropTypes.number };
Ico.defaultProps = { size: 16 };

const IC = {
  x:      'M18 6L6 18M6 6l12 12',
  eye:    'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6',
  edit:   'M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z',
  split:  'M3 3h8v18H3zM13 3h8v18h-8z',
  image:  'M21 19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l2-3h4l2 3h3a2 2 0 0 1 2 2zM12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
  video:  'M23 7l-7 5 7 5V7zM1 5h15v14H1z',
  btn:    'M4 9h16a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z',
  bold:   'M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6zM6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z',
  italic: 'M19 4h-9M14 20H5M14.7 4.7L9.3 19.3',
  list:   'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  table:  'M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18',
  quote:  'M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1zm12 0c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z',
  code:   'M16 18l6-6-6-6M8 6l-6 6 6 6',
  link:   'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
  hr:     'M5 12h14',
};

const TOOLBAR_GROUPS = [
  {
    label: 'Format',
    items: [
      { label: 'H1', title: 'Heading 1', wrap: '# ',   block: true },
      { label: 'H2', title: 'Heading 2', wrap: '## ',  block: true },
      { label: 'H3', title: 'Heading 3', wrap: '### ', block: true },
      { label: 'B',  title: 'Bold',      wrap: '**',   icon: IC.bold   },
      { label: 'I',  title: 'Italic',    wrap: '_',    icon: IC.italic },
      { label: '~~', title: 'Strikethrough', wrap: '~~' },
      { label: '`',  title: 'Inline Code',   wrap: '`' },
    ],
  },
  {
    label: 'Blocks',
    items: [
      { label: 'Bullet',   title: 'Bullet List',   insert: '\n- Item 1\n- Item 2\n- Item 3\n', icon: IC.list  },
      { label: 'Numbered', title: 'Numbered List', insert: '\n1. First\n2. Second\n3. Third\n', icon: IC.list  },
      { label: 'Quote',    title: 'Blockquote',    insert: '\n> Your quote here\n',             icon: IC.quote },
      { label: 'Code',     title: 'Code Block',    insert: '\n```js\n// your code here\n```\n', icon: IC.code  },
      { label: 'Table',    title: 'Table',         insert: '\n| Col 1 | Col 2 |\n|-------|-------|\n| A | B |\n', icon: IC.table },
      { label: 'Link',     title: 'Link',          insert: '[Link text](https://example.com)',  icon: IC.link  },
      { label: 'Divider',  title: 'HR',            insert: '\n---\n',                           icon: IC.hr    },
    ],
  },
  {
    label: 'Callouts',
    items: [
      { label: '💡 Tip',        title: 'Tip',        insert: '\n:::tip\nYour tip here\n:::\n'                     },
      { label: '⚠️ Warning',   title: 'Warning',     insert: '\n:::warning\nWarning message here\n:::\n'          },
      { label: '📖 Definition', title: 'Definition', insert: '\n:::definition\n**Term**: Explanation here\n:::\n' },
      { label: '📌 Important',  title: 'Important',  insert: '\n:::important\nKey point here\n:::\n'              },
      { label: '✅ Remember',   title: 'Remember',   insert: '\n:::remember\nThing to remember\n:::\n'            },
    ],
  },
];

// ── Preview CSS (same as EBookCreator) ────────────────────────────────────────
const PREVIEW_CSS = `
.ebk h1{font-size:2rem;font-weight:800;color:#0f172a;margin-bottom:1rem;border-bottom:3px solid #6366f1;padding-bottom:.5rem}
.ebk h2{font-size:1.5rem;font-weight:700;color:#1e293b;margin-top:2.5rem;margin-bottom:.75rem;padding-left:.75rem;border-left:4px solid #6366f1}
.ebk h3{font-size:1.2rem;font-weight:600;color:#334155;margin-top:1.75rem;margin-bottom:.5rem}
.ebk p{margin-bottom:1rem;color:#374151;line-height:1.85;font-size:1rem}
.ebk ul,.ebk ol{padding-left:1.75rem;margin-bottom:1rem}
.ebk li{margin-bottom:.35rem;color:#374151;line-height:1.7}
.ebk strong{font-weight:700;color:#0f172a}
.ebk blockquote{border-left:4px solid #6366f1;padding:.875rem 1.25rem;background:linear-gradient(to right,#f0f9ff,#fff);margin:1.5rem 0;border-radius:0 .75rem .75rem 0;font-style:italic;color:#475569}
.ebk code{background:#f1f5f9;color:#dc2626;padding:.125rem .375rem;border-radius:.3rem;font-size:.875em;font-family:'Fira Code',monospace}
.ebk pre{background:#0f172a;color:#e2e8f0;padding:1.5rem;border-radius:.75rem;overflow-x:auto;margin-bottom:1.25rem}
.ebk pre code{background:transparent;color:#e2e8f0;padding:0;font-size:.875rem}
.ebk table{width:100%;border-collapse:collapse;margin-bottom:1.25rem;border-radius:.75rem;overflow:hidden}
.ebk th{background:#6366f1;color:#fff;padding:.75rem 1rem;text-align:left;font-weight:600}
.ebk td{padding:.625rem 1rem;border-bottom:1px solid #f1f5f9}
.ebk tr:nth-child(even) td{background:#f8fafc}
.ebk img{max-width:100%;border-radius:.75rem;margin:1rem auto;display:block}
.callout{border-left:4px solid;padding:1rem 1.25rem;border-radius:0 .75rem .75rem 0;margin:1.25rem 0;font-size:.95rem}
.callout-tip{background:#f0fdf4;border-color:#22c55e;color:#14532d}
.callout-warning{background:#fffbeb;border-color:#f59e0b;color:#78350f}
.callout-definition{background:#eff6ff;border-color:#3b82f6;color:#1e3a8a}
.callout-important{background:#fef2f2;border-color:#ef4444;color:#7f1d1d}
.callout-remember{background:#f5f3ff;border-color:#8b5cf6;color:#3b0764}
.callout-title{font-weight:700;margin-bottom:.35rem;font-size:.9rem}
`;

const CALLOUT_META = {
  tip:        { icon: '💡', label: 'Tip' },
  warning:    { icon: '⚠️', label: 'Warning' },
  definition: { icon: '📖', label: 'Definition' },
  important:  { icon: '📌', label: 'Important' },
  remember:   { icon: '✅', label: 'Remember' },
};

function processCustomBlocks(md) {
  return md.replace(/:::(\w+)\n([\s\S]*?):::/g, (_, type, body) => {
    const meta = CALLOUT_META[type];
    if (!meta) return `<div>${body}</div>`;
    return `<div class="callout callout-${type}"><div class="callout-title">${meta.icon} ${meta.label}</div>${body.trim()}</div>`;
  });
}

// ── Toolbar ───────────────────────────────────────────────────────────────────
function Toolbar({ textareaRef, value, onChange, onInsertSpecial }) {
  const insert = useCallback((action) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const sel   = value.slice(start, end);
    let newVal; let cursor;

    if (action.insert) {
      newVal = `${value.slice(0, start)}${action.insert}${value.slice(end)}`;
      cursor = start + action.insert.length;
    } else if (action.block) {
      const ls   = value.lastIndexOf('\n', start - 1) + 1;
      const line = value.slice(ls, end || start);
      newVal = `${value.slice(0, ls)}${action.wrap}${line}${value.slice(end || start)}`;
      cursor = ls + action.wrap.length + line.length;
    } else {
      const word = sel || 'text';
      newVal = `${value.slice(0, start)}${action.wrap}${word}${action.wrap}${value.slice(end)}`;
      cursor = start + action.wrap.length + word.length + action.wrap.length;
    }
    onChange(newVal);
    setTimeout(() => { el.focus(); el.setSelectionRange(cursor, cursor); }, 0);
  }, [value, onChange, textareaRef]);

  return (
    <div className="flex items-center gap-0 border-b border-slate-200 bg-slate-50 overflow-x-auto flex-shrink-0" style={{ minHeight: 44 }}>
      {TOOLBAR_GROUPS.map((group, gi) => (
        <React.Fragment key={group.label}>
          {gi > 0 && <div className="w-px h-5 bg-slate-300 mx-1 flex-shrink-0" />}
          <div className="flex items-center gap-0 px-1">
            {group.items.map((act) => (
              <button key={act.label} type="button" title={act.title} onClick={() => insert(act)}
                className="flex items-center gap-1 px-2 py-1.5 mx-0.5 text-xs font-semibold text-slate-600 rounded hover:bg-indigo-50 hover:text-indigo-700 transition-colors whitespace-nowrap">
                {act.label}
              </button>
            ))}
          </div>
        </React.Fragment>
      ))}
      <div className="w-px h-5 bg-slate-300 mx-1 flex-shrink-0" />
      <div className="flex items-center gap-0 px-1">
        <button type="button" title="Insert Image" onClick={() => onInsertSpecial('image')}
          className="flex items-center gap-1 px-2 py-1.5 mx-0.5 text-xs font-semibold text-slate-600 rounded hover:bg-indigo-50 hover:text-indigo-700 transition-colors">
          <Ico d={IC.image} size={13} /> Image
        </button>
        <button type="button" title="Insert Video" onClick={() => onInsertSpecial('video')}
          className="flex items-center gap-1 px-2 py-1.5 mx-0.5 text-xs font-semibold text-slate-600 rounded hover:bg-indigo-50 hover:text-indigo-700 transition-colors">
          <Ico d={IC.video} size={13} /> Video
        </button>
      </div>
    </div>
  );
}
Toolbar.propTypes = {
  textareaRef:     PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  value:           PropTypes.string.isRequired,
  onChange:        PropTypes.func.isRequired,
  onInsertSpecial: PropTypes.func.isRequired,
};

// ── Insert panels (Image / Video) ─────────────────────────────────────────────
const INPUT_CLS = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white';

function ImageInsertPanel({ onInsert, onClose }) {
  const [url, setUrl] = useState('');
  const [alt, setAlt] = useState('');
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-800">Insert Image</h3>
          <button type="button" onClick={onClose}><Ico d={IC.x} size={18} /></button>
        </div>
        <input type="url" className={INPUT_CLS} placeholder="Image URL" value={url} onChange={(e) => setUrl(e.target.value)} />
        <input type="text" className={INPUT_CLS} placeholder="Alt text" value={alt} onChange={(e) => setAlt(e.target.value)} />
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-600">Cancel</button>
          <button type="button" disabled={!url.trim()} onClick={() => onInsert(`\n<div style="text-align:center;margin:1.25rem 0"><img src="${url}" alt="${alt}" style="max-width:100%;border-radius:.75rem" /></div>\n`)}
            className="flex-1 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white disabled:opacity-40">Insert</button>
        </div>
      </div>
    </div>
  );
}
ImageInsertPanel.propTypes = { onInsert: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired };

function VideoInsertPanel({ onInsert, onClose }) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-800">Insert Video</h3>
          <button type="button" onClick={onClose}><Ico d={IC.x} size={18} /></button>
        </div>
        <input type="url" className={INPUT_CLS} placeholder="YouTube embed URL" value={url} onChange={(e) => setUrl(e.target.value)} />
        <input type="text" className={INPUT_CLS} placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-600">Cancel</button>
          <button type="button" disabled={!url.trim()} onClick={() => onInsert(`\n:::video\nurl=${url}\ntitle=${title || 'Video'}\nwidth=100%\nheight=360\n:::\n`)}
            className="flex-1 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white disabled:opacity-40">Insert</button>
        </div>
      </div>
    </div>
  );
}
VideoInsertPanel.propTypes = { onInsert: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired };

// ── Main ArticleEditorModal ───────────────────────────────────────────────────
export default function ArticleEditorModal({ open, value, onChange, onClose }) {
  const [mode, setMode]   = useState('edit');
  const [panel, setPanel] = useState(null);
  const textareaRef       = useRef(null);

  if (!open) return null;

  const insertAtCursor = (snippet) => {
    const el = textareaRef.current;
    if (!el) { onChange(value + snippet); setPanel(null); return; }
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const nv    = `${value.slice(0, start)}${snippet}${value.slice(end)}`;
    onChange(nv);
    const pos = start + snippet.length;
    setTimeout(() => { el.focus(); el.setSelectionRange(pos, pos); }, 0);
    setPanel(null);
  };

  const processed = processCustomBlocks(value);

  const MODE_BTNS = [
    { key: 'edit',    icon: IC.edit,  label: 'Edit'    },
    { key: 'split',   icon: IC.split, label: 'Split'   },
    { key: 'preview', icon: IC.eye,   label: 'Preview' },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white shadow-sm flex-shrink-0">
        <span className="text-sm font-bold text-slate-700">Article Editor</span>

        <div className="flex items-center bg-slate-100 rounded-xl p-1 gap-0.5 ml-4">
          {MODE_BTNS.map(({ key, icon, label }) => (
            <button key={key} type="button" title={label} onClick={() => setMode(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${mode === key ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
              <Ico d={icon} size={13} />
              <span className="hidden md:inline">{label}</span>
            </button>
          ))}
        </div>

        <button type="button" onClick={onClose}
          className="ml-auto flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">
          Done
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Editor pane */}
        {(mode === 'edit' || mode === 'split') && (
          <div className="flex flex-col min-w-0 overflow-hidden bg-white" style={{ width: mode === 'split' ? '50%' : '100%' }}>
            <Toolbar textareaRef={textareaRef} value={value} onChange={onChange} onInsertSpecial={setPanel} />
            <textarea
              ref={textareaRef}
              className="flex-1 w-full px-8 py-6 font-mono text-sm text-slate-800 bg-white resize-none focus:outline-none leading-loose placeholder-slate-300"
              style={{ fontFamily: "'Fira Code', 'Cascadia Code', monospace", fontSize: '13.5px' }}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Write your article content in Markdown…"
              spellCheck={false}
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  e.preventDefault();
                  const s  = e.target.selectionStart;
                  const nv = `${value.slice(0, s)}  ${value.slice(e.target.selectionEnd)}`;
                  onChange(nv);
                  setTimeout(() => { textareaRef.current.setSelectionRange(s + 2, s + 2); }, 0);
                }
              }}
            />
          </div>
        )}

        {mode === 'split' && <div className="w-1 bg-slate-200 flex-shrink-0" />}

        {/* Preview pane */}
        {(mode === 'preview' || mode === 'split') && (
          <div className="flex flex-col min-w-0 overflow-hidden bg-white" style={{ width: mode === 'split' ? '50%' : '100%' }}>
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200 flex-shrink-0">
              <Ico d={IC.eye} size={13} />
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Preview</span>
            </div>
            <div className="flex-1 overflow-y-auto px-10 py-8">
              <style>{PREVIEW_CSS}</style>
              <div className="ebk max-w-4xl">
                <ReactMarkdown plugins={[gfm]} allowDangerousHtml>{processed}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}
      </div>

      {panel === 'image' && <ImageInsertPanel onInsert={insertAtCursor} onClose={() => setPanel(null)} />}
      {panel === 'video' && <VideoInsertPanel onInsert={insertAtCursor} onClose={() => setPanel(null)} />}
    </div>
  );
}

ArticleEditorModal.propTypes = {
  open:     PropTypes.bool.isRequired,
  value:    PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose:  PropTypes.func.isRequired,
};
